import { computed, type ComputedRef, type Ref } from 'vue'

import type { InsightConfigMeta } from '../models/insightConfig.types'
import {
  appendProcessTransformTemplate,
  buildDefaultPipelineStepConfigText,
  buildPipelineStepEditors,
  type PipelineStepEditor,
} from '../service/insightConfigPipeline'

export interface UseInsightConfigPipeline {
  canAdd: ComputedRef<boolean>
  addStep: () => void
  removeStep: (index: number) => void
  updateType: (index: number, type: string) => void
  updateConfig: (index: number, configText: string) => void
  addTransform: (index: number, transform: string) => void
  resetDefaults: () => void
}

export function useInsightConfigPipeline(
  editors: Ref<PipelineStepEditor[]>,
  meta: () => InsightConfigMeta,
): UseInsightConfigPipeline {
  const replace = (index: number, patch: Partial<PipelineStepEditor>): void => {
    editors.value = editors.value.map((editor, current) =>
      current === index ? { ...editor, ...patch } : editor,
    )
  }

  function addStep(): void {
    const type = meta().pipeline_step_types[0]
    if (!type) return
    editors.value = [
      ...editors.value,
      {
        id: `pipeline-step-new-${editors.value.length + 1}`,
        type,
        configText: buildDefaultPipelineStepConfigText(type),
      },
    ]
  }

  function removeStep(index: number): void {
    editors.value = editors.value.filter((_, current) => current !== index)
  }

  function updateType(index: number, type: string): void {
    replace(index, { type, configText: buildDefaultPipelineStepConfigText(type) })
  }

  function updateConfig(index: number, configText: string): void {
    replace(index, { configText })
  }

  function addTransform(index: number, transform: string): void {
    const editor = editors.value[index]
    if (!editor || !meta().supported_process_transforms.includes(transform)) return
    replace(index, { configText: appendProcessTransformTemplate(editor.configText, transform) })
  }

  function resetDefaults(): void {
    editors.value = buildPipelineStepEditors(
      meta().default_pipeline_steps,
      meta().default_pipeline_steps,
    )
  }

  return {
    canAdd: computed(() => meta().pipeline_step_types.length > 0),
    addStep,
    removeStep,
    updateType,
    updateConfig,
    addTransform,
    resetDefaults,
  }
}
